///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../abstract-command';
import { ActivityDTO } from '../../../../services/project-socket/activity-dto.model';
import { GtthModifyUnitInfoCommand } from './node/modify-unit-info-command';
import { GtthPreloaderModalEvent } from '../../../../shared/preloader-modal/preloader-modal-event';
import { GtthModifyLinkInfoCommand } from './link/modify-link-info-command';
import { GtthLinkFeatureDtoModel } from '../../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { GtthNodeFeatureDtoModel } from '../../../../services/project-socket/dto/gtth-node-feature-dto.model';

export class GtthEnableMultipleEntitiesCommand extends GtthAbstractCommand {
    private linkIds: number[];
    private terminalIds: number[];

    private terminalUnitsToEnable: GtthNodeFeatureDtoModel[];
    private linksToEnable: GtthLinkFeatureDtoModel[];

    getCommandName(): string {
        return 'GtthEnableMultipleEntitiesCommand';
    }

    createFromNodeId(terminalId: number) {
        this.terminalIds = [terminalId];
        return this;
    }

    createFromLinkId(linkId: number) {
        this.linkIds = [linkId];
        return this;
    }

    createFromList(terminalIds: number[], linkIds: number[]) {
        this.terminalIds = terminalIds;
        this.linkIds = linkIds;
        return this;
    }

    doCommand(): void {
        this.historyService.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.show('Loading')
        });

        const logicalTopology = this.historyService.logicalTopology;

        if (!this.linkIds || !this.terminalIds) {
            this.linkIds = this.linkIds || [];
            this.terminalIds = this.terminalIds || [];

            for (const link of this.historyService.logicalTopology.getSelectedLinks()) {
                this.linkIds.push(link.getId());
            }
            for (const station of this.historyService.logicalTopology.getSelectedStations()) {
                this.terminalIds.push(station.getId());
            }
        }

        const terminalIdsToEnable = new Set<number>();
        this.linksToEnable = [];
        this.terminalUnitsToEnable = [];

        this.terminalIds ??= [];
        this.terminalIds.forEach((entityId: number) => {
            const terminalStation = logicalTopology.getStation(entityId);

            if (!terminalStation.isEnabled()) {
                const terminalGeoJson = terminalStation.toGeoJson();
                terminalGeoJson.setIsEnabled(true);
                this.terminalUnitsToEnable.push(terminalGeoJson);
                terminalIdsToEnable.add(terminalStation.getId());
            }

            logicalTopology.getAllStationLinks(entityId).forEach((link) => {
                if (link.getLinkIsDisabled()) {
                    const linkExist = this.linkIds.find((innerLinkId) => {
                        return innerLinkId === link.getId();
                    });
                    if (!linkExist) {
                        this.linkIds.push(link.getId());
                    }
                }
            });
        });

        this.linkIds ??= [];
        this.linkIds.forEach((entityId: number) => {
            const linkToEnable = logicalTopology.getLink(entityId);
            const linkGeoJson = linkToEnable.toGeoJson();

            if (linkToEnable.getLinkIsDisabled()) {
                linkGeoJson.setIsDisabled(false);
                const nodeFrom = linkToEnable.getFromStation();
                const nodeTo = linkToEnable.getToStation();

                if (
                    (nodeFrom.isEnabled() || terminalIdsToEnable.has(nodeFrom.getId())) &&
                    (nodeTo.isEnabled() || terminalIdsToEnable.has(nodeTo.getId()))
                ) {
                    this.linksToEnable.push(linkGeoJson);
                }
            }
        });

        const activity = this.createActivityDTO(false);
        activity.links = [...this.linksToEnable];
        activity.nodes = [...this.terminalUnitsToEnable];
        this.sendActivity(activity);
    }

    undoCommand(): void {
        this.historyService.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.show('Loading')
        });

        this.terminalUnitsToEnable.forEach((terminalGeoJson: GtthNodeFeatureDtoModel) => terminalGeoJson.setIsEnabled(false));

        this.linksToEnable.forEach((linkGeoJson: GtthLinkFeatureDtoModel) => linkGeoJson.setIsDisabled(true));

        const activity = this.createActivityDTO(false);
        activity.links = [...this.linksToEnable];
        activity.nodes = [...this.terminalUnitsToEnable];
        this.sendActivity(activity);
    }

    doCommandActivity(activityDto: ActivityDTO): void {
        this.handleCommandActivity(activityDto);
    }

    undoCommandActivity(activityDto: ActivityDTO): void {
        this.handleCommandActivity(activityDto);
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    isModifyTopology(): boolean {
        return true;
    }

    isClearDesign(): boolean {
        return true;
    }

    private handleCommandActivity(activityDto: ActivityDTO): void {
        if (!activityDto.successful) {
            this.historyService.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.hide()
            });

            this.historyService.notificationService.showError(activityDto?.error?.message);

            if (this.terminalUnitsToEnable && this.linksToEnable) {
                console.log('Command failed:', activityDto.error);
            }

            if (this.commandResolve) {
                this.commandResolve(activityDto.successful);
            }
        } else {
            this.changeEntities(activityDto.nodes, activityDto.links).then((result: boolean) => {
                if (this.commandResolve) {
                    this.commandResolve(result);
                }

                this.historyService.eventManager.broadcast({
                    name: 'togglePreloaderModal',
                    eventObject: GtthPreloaderModalEvent.hide()
                });
            });
        }
    }

    private changeEntities(nodes: GtthNodeFeatureDtoModel[], links: GtthLinkFeatureDtoModel[]): Promise<boolean> {
        const nodesDisableCommands = [];
        const linksDisableCommands = [];

        nodes ??= [];
        nodes.forEach((terminalGeoJson: GtthNodeFeatureDtoModel) => {
            const unitModifyCommand = this.historyService.command(GtthModifyUnitInfoCommand).create(terminalGeoJson.getId());
            unitModifyCommand.setNewIsEnableValue(terminalGeoJson.getIsEnabled());
            nodesDisableCommands.push(this.historyService.doCommandBypassHistory(unitModifyCommand, true));
        });

        return Promise.all(nodesDisableCommands).then((_nodesDisableResult) => {
            links ??= [];
            links.forEach((linkGeoJson: GtthLinkFeatureDtoModel) => {
                const linkId = linkGeoJson.getId();
                const linkModifyCommand = this.historyService.command(GtthModifyLinkInfoCommand).create(linkId);
                linkModifyCommand.setOldLinkIsDisable(this.historyService.logicalTopology.getLink(linkId).getLinkIsDisabled());
                linkModifyCommand.setNewLinkIsDisable(linkGeoJson.getIsDisabled());
                linksDisableCommands.push(this.historyService.doCommandBypassHistory(linkModifyCommand, true));
            });

            return Promise.all(linksDisableCommands).then((_linksDisableResult) => {
                return true;
            });
        });
    }
}
